import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { User, Mail, Phone, MapPin, Lock, Shield, AlertCircle, Check, Smartphone, Key, Download, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountSettingsPage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // MFA state
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableMfaForm, setDisableMfaForm] = useState({ password: '', code: '' });
  const [showDisableMfa, setShowDisableMfa] = useState(false);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: ''
  });

  // Email change form state
  const [emailForm, setEmailForm] = useState({ new_email: '' });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchMfaStatus();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/account/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch profile');

      const data = await response.json();
      setProfile(data.profile);
      setPendingEmail(data.pending_email_change);
      
      // Populate form
      setProfileForm({
        first_name: data.profile.first_name || '',
        last_name: data.profile.last_name || '',
        phone: data.profile.phone || '',
        address_line1: data.profile.address_line1 || '',
        address_line2: data.profile.address_line2 || '',
        city: data.profile.city || '',
        state: data.profile.state || '',
        zip_code: data.profile.zip_code || ''
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchMfaStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mfa/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMfaStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch MFA status:', err);
    }
  };

  const handleEnableMfa = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mfa/setup/begin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to start MFA setup');

      setMfaSetup(data);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmMfa = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mfa/setup/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ verification_code: mfaCode })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Invalid verification code. Please try again.');

      setRecoveryCodes(data.recovery_codes);
      setMfaSetup(null);
      setMfaCode('');
      setMessage({ type: 'success', text: 'MFA Enabled' });
      fetchMfaStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDisableMfa = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mfa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: disableMfaForm.password,
          verification_code: disableMfaForm.code
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to disable MFA');

      setMessage({ type: 'success', text: 'Multi-Factor Authentication has been disabled.' });
      setShowDisableMfa(false);
      setDisableMfaForm({ password: '', code: '' });
      fetchMfaStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateRecoveryCodes = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mfa/recovery-codes/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to regenerate recovery codes');

      setRecoveryCodes(data.recovery_codes);
      setMessage({ type: 'success', text: 'Recovery codes regenerated successfully.' });
      fetchMfaStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const downloadRecoveryCodes = () => {
    if (!recoveryCodes) return;
    
    const content = `BlueLock Freights - MFA Recovery Codes
Generated: ${new Date().toISOString()}

KEEP THESE CODES SAFE!
Each code can only be used once.

${recoveryCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

If you lose access to your authenticator app, use one of these codes to log in.
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bluelock-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/account/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileForm)
      });

      const data = await response.json();
      
      if (!response.ok) throw new Error(data.detail || 'Failed to update profile');

      setMessage({ type: 'success', text: 'Your profile information has been updated.' });
      fetchProfile();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/account/email-change-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailForm)
      });

      const data = await response.json();
      
      if (!response.ok) throw new Error(data.detail || 'Failed to submit request');

      setMessage({ type: 'success', text: 'Your email change request has been submitted for review.' });
      setEmailForm({ new_email: '' });
      fetchProfile();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setSaving(false);
      return;
    }

    if (passwordForm.new_password.length < 12) {
      setMessage({ type: 'error', text: 'Password must be at least 12 characters' });
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      const data = await response.json();
      
      if (!response.ok) throw new Error(data.detail || 'Failed to change password');

      setMessage({ type: 'success', text: 'Your password has been updated.' });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="account-settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <User className="w-7 h-7 text-blue-600" />
            Account Settings
          </h1>
          <p className="text-gray-600 mt-1">
            Update your contact information and security settings. Changes may be logged for compliance and security.
          </p>
        </div>

        {/* Communication Preferences link (M3 F2) */}
        <a
          href="/communication-preferences"
          data-testid="account-comm-prefs-link"
          className="block bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:border-blue-300 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Communication Preferences</p>
              <p className="text-sm text-gray-500">Choose how we contact you (email / SMS).</p>
            </div>
            <span className="text-blue-600 text-sm font-medium">Manage →</span>
          </div>
        </a>

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-lg flex items-start gap-3 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            )}
            <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </p>
          </div>
        )}

        {/* Section 1: Profile Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
          </div>

          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  placeholder="First"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  placeholder="Last"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="last-name-input"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">Used for internal records and notes.</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number
              </label>
              <input
                type="tel"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                placeholder="(###) ###-####"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="phone-input"
              />
              <p className="text-xs text-gray-500 mt-1">Used for operational contact and notifications (if enabled).</p>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Address (Optional)
              </label>
              <div className="space-y-3">
                <input
                  type="text"
                  value={profileForm.address_line1}
                  onChange={(e) => setProfileForm({ ...profileForm, address_line1: e.target.value })}
                  placeholder="Address Line 1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="address1-input"
                />
                <input
                  type="text"
                  value={profileForm.address_line2}
                  onChange={(e) => setProfileForm({ ...profileForm, address_line2: e.target.value })}
                  placeholder="Address Line 2 (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="address2-input"
                />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={profileForm.city}
                    onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                    placeholder="City"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="city-input"
                  />
                  <input
                    type="text"
                    value={profileForm.state}
                    onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                    placeholder="State"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="state-input"
                  />
                  <input
                    type="text"
                    value={profileForm.zip_code}
                    onChange={(e) => setProfileForm({ ...profileForm, zip_code: e.target.value })}
                    placeholder="ZIP"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="zip-input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Optional. Used for internal records only.</p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="save-profile-btn"
              >
                {saving ? 'Saving...' : 'Save Profile Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* Section 2: Email Address */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Email Address</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Your email address is used for sign-in and account notifications.
          </p>

          {pendingEmail && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Pending request:</strong> Change to <code className="bg-yellow-100 px-1 rounded">{pendingEmail.new_email}</code>
                <br />
                <span className="text-xs">Submitted {new Date(pendingEmail.requested_at).toLocaleDateString()}</span>
              </p>
            </div>
          )}

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Email</label>
              <input
                type="email"
                value={profile?.email || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                data-testid="current-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Email Address</label>
              <input
                type="email"
                value={emailForm.new_email}
                onChange={(e) => setEmailForm({ new_email: e.target.value })}
                placeholder="new.email@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="new-email-input"
              />
            </div>

            <p className="text-xs text-gray-500">
              For security, email changes may require verification and administrator approval.
            </p>

            <button
              type="submit"
              disabled={saving || !emailForm.new_email || pendingEmail}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
              data-testid="request-email-btn"
            >
              Request Email Change
            </button>
          </form>
        </div>

        {/* Section 3: Password */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Password</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Change your password to keep your account secure.
          </p>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="confirm-password-input"
              />
            </div>

            <p className="text-xs text-gray-500">
              Use at least 12 characters with a mix of uppercase, lowercase, numbers, and a symbol.
            </p>

            <button
              type="submit"
              disabled={saving || !passwordForm.current_password || !passwordForm.new_password}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              data-testid="update-password-btn"
            >
              {saving ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        {/* Section 4: Multi-Factor Authentication (MFA) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Multi-Factor Authentication (MFA)</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            MFA adds an extra layer of security by requiring a one-time verification code during login.
          </p>

          {/* MFA Status Display */}
          <div className={`rounded-lg p-4 mb-4 ${mfaStatus?.mfa_enabled ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
            <p className="text-sm font-medium">
              MFA Status: <span className={mfaStatus?.mfa_enabled ? 'text-green-700' : 'text-gray-700'}>
                {mfaStatus?.mfa_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </p>
            {user?.role === 'super_admin' && !mfaStatus?.mfa_enabled && (
              <p className="text-xs text-red-600 mt-1 font-medium inline-flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> MFA is required for Super Admin accounts
              </p>
            )}
          </div>

          {/* Recovery Codes Display */}
          {recoveryCodes && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2 mb-3">
                <Key className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-amber-900">Recovery Codes</h4>
                  <p className="text-xs text-amber-800 mt-1">
                    Save these recovery codes in a secure location. Each code can be used once if you lose access to your authenticator app.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {recoveryCodes.map((code, i) => (
                  <code key={i} className="bg-white px-2 py-1 rounded text-sm font-mono text-gray-800">
                    {code}
                  </code>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={downloadRecoveryCodes}
                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
                >
                  <Download className="w-4 h-4" />
                  Download Recovery Codes
                </button>
                <button
                  onClick={() => setRecoveryCodes(null)}
                  className="px-3 py-1.5 text-amber-700 text-sm hover:underline"
                >
                  I've saved these codes
                </button>
              </div>
              <p className="text-xs text-amber-700 mt-2 font-medium inline-flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> Recovery codes will not be shown again.
              </p>
            </div>
          )}

          {/* MFA Not Enabled State */}
          {mfaStatus && !mfaStatus.mfa_enabled && !mfaSetup && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">MFA is currently disabled for your account.</p>
                <p className="text-xs text-gray-500 mt-1">
                  Enabling MFA helps protect your account from unauthorized access, even if your password is compromised.
                </p>
              </div>
              <button
                onClick={handleEnableMfa}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="enable-mfa-btn"
              >
                Enable MFA
              </button>
            </div>
          )}

          {/* MFA Setup Flow */}
          {mfaSetup && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Set Up Multi-Factor Authentication</h3>
              <p className="text-sm text-gray-600">
                Scan this code using an authenticator app (Google Authenticator, Authy, Microsoft Authenticator).
              </p>
              
              <div className="flex flex-col items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <img 
                  src={mfaSetup.qr_code} 
                  alt="MFA QR Code" 
                  className="w-48 h-48 border border-gray-200 rounded-lg"
                />
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Can't scan? Enter this code manually:</p>
                  <code className="text-sm bg-white px-3 py-1 rounded border font-mono">{mfaSetup.secret}</code>
                </div>
              </div>

              <form onSubmit={handleConfirmMfa} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter 6-digit verification code
                  </label>
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-lg tracking-widest"
                    data-testid="mfa-code-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={saving || mfaCode.length !== 6}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    data-testid="confirm-mfa-btn"
                  >
                    {saving ? 'Verifying...' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMfaSetup(null); setMfaCode(''); }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* MFA Enabled State */}
          {mfaStatus?.mfa_enabled && !mfaSetup && (
            <div className="space-y-4">
              <div className="bg-green-50 rounded-lg p-4 flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">MFA is enabled for your account.</p>
                  <p className="text-xs text-green-700 mt-1">
                    You will be required to enter a one-time verification code when signing in.
                  </p>
                  {mfaStatus.recovery_codes_remaining !== undefined && (
                    <p className="text-xs text-green-700 mt-1">
                      Recovery codes remaining: {mfaStatus.recovery_codes_remaining}
                    </p>
                  )}
                </div>
              </div>

              {/* Disable MFA Form */}
              {showDisableMfa ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-red-800 mb-2">Disable MFA</h4>
                  <p className="text-xs text-red-700 mb-3">
                    To disable MFA, confirm your password and enter a valid verification code.
                  </p>
                  <form onSubmit={handleDisableMfa} className="space-y-3">
                    <input
                      type="password"
                      value={disableMfaForm.password}
                      onChange={(e) => setDisableMfaForm({ ...disableMfaForm, password: e.target.value })}
                      placeholder="Current Password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      value={disableMfaForm.code}
                      onChange={(e) => setDisableMfaForm({ ...disableMfaForm, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                      placeholder="Verification Code"
                      maxLength={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    />
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={saving || !disableMfaForm.password || disableMfaForm.code.length !== 6}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                        data-testid="confirm-disable-mfa-btn"
                      >
                        Disable MFA
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowDisableMfa(false); setDisableMfaForm({ password: '', code: '' }); }}
                        className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDisableMfa(true)}
                    className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors text-sm"
                    data-testid="disable-mfa-btn"
                  >
                    Disable MFA
                  </button>
                  <button
                    onClick={handleRegenerateRecoveryCodes}
                    disabled={saving}
                    className="flex items-center gap-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
                    data-testid="regenerate-codes-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate Recovery Codes
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 5: Security Notice */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Security Notice</h3>
              <p className="text-sm text-gray-600">
                For security and compliance purposes, account changes may be logged. If you believe your account has been accessed improperly, notify an administrator immediately.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AccountSettingsPage;
